import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import { useForm } from "react-hook-form";
import { useAuth } from "./../util/auth";
import { getUser, updateUser } from "./../util/db";

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

function SettingsGeneral(props) {
  const auth = useAuth();
  const [pending, setPending] = useState(false);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    if (auth.user?.uid) {
      getUser(auth.user.uid).then(data => {
        setUserData(data);
      }).catch(err => console.error('Error loading user data:', err));
    }
  }, [auth.user?.uid]);

  const { register, handleSubmit, formState: { errors }, reset } = useForm({
    defaultValues: {
      name: auth.user?.displayName || '',
      email: auth.user?.email || '',
      homeState: ''
    }
  });

  useEffect(() => {
    if (userData) {
      reset({
        name: auth.user?.displayName || '',
        email: auth.user?.email || '',
        homeState: userData.homeState || ''
      });
    }
  }, [userData, auth.user, reset]);

  const onSubmit = async (data) => {
    setPending(true);

    try {
      // Update auth profile (name and email)
      await auth.updateProfile({ name: data.name, email: data.email });

      // Update Firestore user document (homeState)
      if (auth.user?.uid) {
        await updateUser(auth.user.uid, {
          homeState: data.homeState,
          name: data.name,
          email: data.email
        });

        // Reload user data to update the context
        await auth.refreshUserData();
        const updatedUserData = await getUser(auth.user.uid);
        setUserData(updatedUserData);
      }

      props.onStatus({
        type: "success",
        message: "Your profile has been updated",
      });
    } catch (error) {
      if (error.code === "auth/requires-recent-login") {
        props.onStatus({
          type: "requires-recent-login",
          callback: () => onSubmit(data),
        });
      } else {
        props.onStatus({
          type: "error",
          message: error.message,
        });
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <TextField
            variant="outlined"
            type="text"
            label="Name"
            error={!!errors.name}
            helperText={errors.name?.message}
            fullWidth
            {...register("name", { required: "Please enter your name" })}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            variant="outlined"
            type="email"
            label="Email"
            error={!!errors.email}
            helperText={errors.email?.message}
            fullWidth
            {...register("email", { required: "Please enter your email" })}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            select
            variant="outlined"
            label="Home State"
            error={!!errors.homeState}
            helperText={errors.homeState?.message || "Used to filter bonuses by your location"}
            fullWidth
            {...register("homeState")}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {US_STATES.map((state) => (
              <MenuItem key={state} value={state}>
                {state}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            type="submit"
            disabled={pending}
            fullWidth
          >
            {!pending && <span>Save</span>}
            {pending && <CircularProgress size={28} />}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}

export default SettingsGeneral;
